import { useTheme } from "@emotion/react";
import {
    Box,
    Grid,
    Typography
} from "@mui/material";
import { useState } from "react";
import { useMicrospecDynamicUIContext } from "../GlobalProvider";
import { useMicrospecThemeLanguageContext } from "../Themes";
import SupportedAuthProviders from "./MicrospecSupportedAuthProvider";
import { renderProviderButtons } from "./ProviderComponentMapper";
import { renderProviderIdentifierComponent } from "./ProviderIdentifierMapper";

const LoginIdentifierFirst = ({
    loginMethod,
    identifier,
    error,
    loading,
    signIn,
    redirectTo,
    enabledProviders = [],
    enableRegister = true,
    enabledCountryCodes = [],
    registerMode = false,
    onMethodSelect,
    onIdentifierChange,
    onSubmit,
    onUserFound,
    onUserNotFound,
    onSetCountryCode,
    onStartLoading
}) => {
    const { cloudProvider } = useMicrospecDynamicUIContext();
    const { userProfile } = cloudProvider;
    const { methods } = userProfile;

    const { localizationProvider } = useMicrospecThemeLanguageContext();
    const theme = useTheme();

    // Store current country code for phone login
    const [currentCountryCode, setCurrentCountryCode] = useState(
        enabledCountryCodes.length > 0 ? enabledCountryCodes[0].label : '+972'
    );

    // Generate gradients from theme colors
    const primaryGradient = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`;

    // Handle country code changes
    const handleCountryCodeChange = (countryCode) => {
        setCurrentCountryCode(countryCode);
        onSetCountryCode(countryCode);
    };

    // Handle identifier submission with user lookup
    const handleIdentifierSubmit = async (event) => {
        event.preventDefault();

        if (!identifier.trim()) {
            return;
        }

        // Start loading
        onStartLoading();

        try {
            // For phone login, combine country code with phone number
            let fullIdentifier = identifier;
            if (loginMethod === SupportedAuthProviders.PHONE) {
                // Remove any leading zeros and add country code
                const cleanPhoneNumber = identifier.replace(/^0+/, '');
                fullIdentifier = `${currentCountryCode}${cleanPhoneNumber}`;
            }

            // Call getUserInfo to check if user exists
            const userInfo = await methods.getUserInfo(fullIdentifier);

            if (userInfo && userInfo.exists) {
                // User exists, proceed to next step based on login method
                onUserFound(userInfo, loginMethod);
            } else {
                // User doesn't exist, allow registration for both phone and email
                console.log('User not found, proceeding to registration for method:', loginMethod);
                onUserNotFound();
            }
        } catch (error) {
            console.error('Error checking user:', error);
            onSubmit(event, {
                error: localizationProvider.translateByType({
                    type: "Internal",
                    text: "errorCheckingUser"
                }) || "حدث خطأ أثناء التحقق من المستخدم. يرجى المحاولة مرة أخرى."
            });
        }
    };

    // Only show provider selection if more than one provider is enabled
    const showProviderSelection = enabledProviders.length > 1;

    return (
        <Box>
            {/* Method Selection Buttons - Only show if multiple providers are enabled */}
            {showProviderSelection && (
                <>
                    <Typography
                        variant="body2"
                        sx={{
                            textAlign: 'center',
                            mb: 2,
                            fontWeight: 500
                        }}
                    >
                        {localizationProvider.translateByType({
                            type: "Internal",
                            text: "chooseLoginMethod"
                        })}
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {renderProviderButtons(enabledProviders, {
                            selectedProvider: loginMethod,
                            onMethodSelect,
                            theme,
                            primaryGradient,
                            localizationProvider
                        })}
                    </Grid>
                </>
            )}

            {/* Render identifier input based on selected method using provider mapper */}
            {renderProviderIdentifierComponent(loginMethod, {
                identifier,
                error,
                loading,
                signIn,
                redirectTo,
                enableRegister,
                enabledCountryCodes,
                registerMode,
                onIdentifierChange,
                onSubmit: handleIdentifierSubmit,
                ...(loginMethod === SupportedAuthProviders.PHONE && {
                    onSetCountryCode: handleCountryCodeChange
                })
            })}
        </Box>
    );
};

export default LoginIdentifierFirst;